// Business Web team-collaboration media-capability layer (P0 — NotFoundError fix).
// One place that acquires local call media with device awareness + precise error classification, so
// no calling control shows the old generic "Failed to access camera/microphone" when the real cause
// is a missing camera, a busy device, or a denied permission.
//
// Design facts proven from the runtime evidence at https://app-staging.servisfy.com/business/voice-calls:
//   - The failing constraint was getUserMedia({ audio:true, video:{640x480} }) on a workstation that has
//     a microphone but NO camera (Chrome Incognito) → NotFoundError: Requested device not found.
//   - The codebase stores NO selected deviceId anywhere, so there is no stale-deviceId vector here; the
//     acquire helper still validates any caller-supplied deviceId against the live enumeration and drops
//     stale ids defensively (used by tests / future device pickers).
//   - An audio call already requests { audio:true, video:false } and must never require a camera.

// Error codes surfaced to the UI (mapped 1:1 from the browser error name).
export const MEDIA_ERROR = {
  PERMISSION_DENIED: 'PERMISSION_DENIED',     // NotAllowedError — permission denied or browser policy
  NO_DEVICE: 'NO_DEVICE',                     // NotFoundError — requested device does not exist
  OVERCONSTRAINED: 'OVERCONSTRAINED',         // OverconstrainedError — device/constraint unavailable
  DEVICE_BUSY: 'DEVICE_BUSY',                 // NotReadableError — device exists but busy/unreadable
  ABORTED: 'ABORTED',                         // AbortError — acquisition aborted
  UNSUPPORTED: 'UNSUPPORTED',                 // getUserMedia/getDisplayMedia not available
  UNKNOWN: 'UNKNOWN',                         // anything else — exact safe message retained
};

// Build an Error carrying a stable code + a user-facing (device-id-free) message.
function mediaError(code, userMessage, cause) {
  const e = new Error(userMessage);
  e.code = code;
  e.userMessage = userMessage;
  e.name = (cause && cause.name) || code;
  if (cause && cause.message) e.rawMessage = cause.message;
  return e;
}

// Map a browser MediaError to a code + actionable message. `wantVideo` tunes the missing-device text.
export function classifyMediaError(err, { wantVideo = false } = {}) {
  const name = (err && err.name) || '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return mediaError(MEDIA_ERROR.PERMISSION_DENIED,
        wantVideo
          ? 'Camera/microphone access is blocked. Allow it in your browser site settings.'
          : 'Microphone access is blocked. Allow it in your browser site settings.',
        err);
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return mediaError(MEDIA_ERROR.NO_DEVICE,
        wantVideo ? 'No camera was found on this computer.' : 'No microphone was found on this computer.',
        err);
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return mediaError(MEDIA_ERROR.OVERCONSTRAINED,
        'The selected device is no longer available. The default device will be used.', err);
    case 'NotReadableError':
    case 'TrackStartError':
      return mediaError(MEDIA_ERROR.DEVICE_BUSY,
        'The microphone or camera appears to be in use by another application.', err);
    case 'AbortError':
      return mediaError(MEDIA_ERROR.ABORTED, 'Media access was interrupted. Please try again.', err);
    default:
      return mediaError(MEDIA_ERROR.UNKNOWN,
        'Could not start media on this device. Please try again.', err);
  }
}

// Count live audio/video inputs. Pre-permission browsers report entries with empty labels but the
// COUNT is reliable for presence, which is all we need to decide the audio-only fallback.
export async function probeDevices(mediaDevices = (typeof navigator !== 'undefined' ? navigator.mediaDevices : null)) {
  const out = { audioInputCount: 0, videoInputCount: 0, audioInputIds: [], videoInputIds: [] };
  if (!mediaDevices || !mediaDevices.enumerateDevices) return out;
  try {
    const devices = await mediaDevices.enumerateDevices();
    for (const d of devices || []) {
      if (d.kind === 'audioinput') { out.audioInputCount++; if (d.deviceId) out.audioInputIds.push(d.deviceId); }
      else if (d.kind === 'videoinput') { out.videoInputCount++; if (d.deviceId) out.videoInputIds.push(d.deviceId); }
    }
  } catch (e) { /* enumeration best-effort — treat as unknown counts */ }
  return out;
}

// Drop a caller-supplied deviceId that no longer exists in the live enumeration (stale-id guard).
function validDeviceId(id, liveIds) {
  if (!id) return undefined;
  return liveIds.indexOf(id) !== -1 ? id : undefined;
}

// Acquire local media for a team call with device awareness.
//   - Audio call (wantVideo=false): requests audio only; NEVER requires a camera.
//   - Video call (wantVideo=true) with a camera: requests audio + video.
//   - Video call with NO camera: falls back to an audio-only stream and reports degraded:'no_camera'
//     so the caller can proceed as an audio call and show an inline notice (no generic failure).
//   - A stored/selected deviceId that no longer exists is cleared and the default device is used.
//   - At most ONE fallback retry — never loops.
// Returns { stream, video: boolean, degraded: null | 'no_camera' } or throws a classified media Error.
export async function acquireCallMedia({
  wantVideo = false,
  audioDeviceId = null,
  videoDeviceId = null,
  mediaDevices = (typeof navigator !== 'undefined' ? navigator.mediaDevices : null),
} = {}) {
  if (!mediaDevices || !mediaDevices.getUserMedia) {
    throw mediaError(MEDIA_ERROR.UNSUPPORTED, 'This browser does not support audio/video calls.');
  }
  const probe = await probeDevices(mediaDevices);

  // No camera present at all → don't even request video; go straight to audio-only (degraded).
  const cameraPresent = probe.videoInputCount > 0;
  const effectiveWantVideo = wantVideo && cameraPresent;
  let degraded = wantVideo && !cameraPresent ? 'no_camera' : null;

  const aId = validDeviceId(audioDeviceId, probe.audioInputIds);
  const vId = validDeviceId(videoDeviceId, probe.videoInputIds);

  const buildConstraints = (withVideo) => ({
    audio: aId ? { deviceId: { exact: aId } } : true,
    video: withVideo ? { width: 640, height: 480, ...(vId ? { deviceId: { exact: vId } } : {}) } : false,
  });

  try {
    const stream = await mediaDevices.getUserMedia(buildConstraints(effectiveWantVideo));
    return { stream, video: effectiveWantVideo, degraded };
  } catch (err) {
    const classified = classifyMediaError(err, { wantVideo: effectiveWantVideo });
    // A video acquisition that failed for a device/constraint reason should not kill the call when a
    // microphone is available — retry audio-only ONCE and continue as an audio call.
    const deviceReason = classified.code === MEDIA_ERROR.NO_DEVICE || classified.code === MEDIA_ERROR.OVERCONSTRAINED;
    if (effectiveWantVideo && deviceReason) {
      try {
        const audioStream = await mediaDevices.getUserMedia({ audio: aId ? { deviceId: { exact: aId } } : true, video: false });
        return { stream: audioStream, video: false, degraded: 'no_camera' };
      } catch (err2) {
        throw classifyMediaError(err2, { wantVideo: false });
      }
    }
    throw classified;
  }
}
